import React, { Component } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import styled, { StyledComponent, css } from 'styled-components';
import { Button, Grid, Checkbox, Label, Theme, Colors, Pagination, AnalyticsProps } from '@inplayer-org/inplayer-ui';

// utils
import isEqual from 'lodash/isEqual';
import truncate from 'lodash/truncate';
import isEmpty from 'lodash/isEmpty';

// actions
import {
  loadInitialExternalAssets as loadInitialExternalAssetsAction,
  loadBrightcoveExternalAssets as loadBrightcoveExternalAssetsAction,
  resetExternalAssets as resetExternalAssetsAction,
  LoadExternalAssetsParams,
} from 'ducks/merchantDucks/items/actions';

// types
import RootState from 'ducks/RootState';
import ItemsState from 'ducks/merchantDucks/items/types';

// components
import StyledContainer from 'components/StyledContainer';
import Loader from 'components/Loader';

// images
import previewImg from 'assets/pics/ip-preview-premium.jpg';

const { Container, Cell } = Grid;

interface OvpVideoBoxProps extends AnalyticsProps {
  checked?: boolean;
}

const sharedBoxStyle = () => css`
  box-shadow: 0 0.2rem 0.5rem ${Colors.gray};
  transform: scale(1.05);
`;

export const OvpVideoBox: StyledComponent<'div', Theme, OvpVideoBoxProps> = styled.div<OvpVideoBoxProps>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  transition: transform 0.3s ease-in-out;

  ${({ checked }) =>
    checked &&
    css`
      ${sharedBoxStyle}
    `}

  &:hover {
    ${sharedBoxStyle}
  }
`;

export const Box: StyledComponent<'div', Theme, OvpVideoBoxProps> = styled.div<OvpVideoBoxProps>`
  text-align: center;
  margin: 1.5rem 1rem;
  cursor: pointer;
`;

export const VideoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoCheckbox = styled(Checkbox)`
  position: absolute;
  margin: 0.2rem 9.5rem 9.5rem 0.5rem;
`;

interface DispatchProps {
  loadInitialExternalAssets: (params: LoadExternalAssetsParams) => any;
  loadBrightcoveExternalAssets: (params: LoadExternalAssetsParams) => any;
  resetExternalAssets: () => any;
}

interface OwnProps {
  ovpType: string;
  accessControlType: number;
  closeModal: () => void;
  isJwPlayerSync: boolean;
  onCompleteSelection: (checkedVideos: Record<string, boolean>, syncAll: boolean) => () => any;
  onBackButtonClick: () => void;
}

interface StateProps {
  items: ItemsState;
}

interface State {
  page: number;
  checkedVideos: Record<string, boolean>;
  syncAll: boolean;
}

type Props = OwnProps & StateProps & DispatchProps;

export class OvpOptionContent extends Component<Props, State> {
  state: State = {
    page: 1,
    checkedVideos: {},
    syncAll: false,
  };

  componentDidMount() {
    const { resetExternalAssets, loadInitialExternalAssets, ovpType } = this.props;
    const page = ovpType === 'jwplayer' ? 1 : 0;
    resetExternalAssets();
    loadInitialExternalAssets({ type: ovpType, page, limit: 8 });
  }

  componentDidUpdate(prevProps: Props) {
    const {
      ovpType: prevOvpType,
      items: { externalAssets: prevExternalAssets },
    } = prevProps;
    const {
      ovpType,
      loadInitialExternalAssets,
      items: {
        externalAssets: { videos },
      },
    } = this.props;
    const { checkedVideos, syncAll } = this.state;

    if (ovpType !== prevOvpType) {
      const page = ovpType === 'jwplayer' ? 1 : 0;
      loadInitialExternalAssets({ type: ovpType, page, limit: 8 });
    }

    if (!isEqual(prevExternalAssets.videos, videos) && syncAll) {
      this.setState({
        checkedVideos: {
          ...checkedVideos,
          ...videos.reduce((obj, e) => ({ ...obj, [e.id || e.key]: syncAll }), {}),
        },
      });
    }
  }

  selectPage = (page: number): void => {
    const { loadBrightcoveExternalAssets, ovpType, loadInitialExternalAssets } = this.props;
    this.setState({ page });
    if (ovpType === 'jwplayer') {
      loadInitialExternalAssets({ type: ovpType, page, limit: 8 });
    } else {
      loadBrightcoveExternalAssets({ type: ovpType, page: page - 1, limit: 8 });
    }
  };

  handleCheckAll = (isChecked: boolean): void => {
    const {
      items: {
        externalAssets: { videos },
      },
    } = this.props;

    this.setState({
      syncAll: isChecked,
      checkedVideos: videos.reduce((obj, e) => ({ ...obj, [e.id || e.key]: isChecked }), {}),
    });
  };

  toggleCheckbox = (id: number) => () => {
    this.setState(({ checkedVideos }) => ({
      checkedVideos: {
        ...checkedVideos,
        [id]: !checkedVideos[id],
      },
    }));
  };

  getImagePreview = (images: any) => {
    if (!isEmpty(images)) {
      const { poster, thumbnail } = images;
      return poster ? poster.src : thumbnail.src;
    }
    return previewImg;
  };

  renderContent = () => {
    const {
      items: {
        externalAssets: { videos, total },
      },
      isJwPlayerSync,
      onCompleteSelection,
      onBackButtonClick,
      ovpType,
    } = this.props;

    const { checkedVideos: stateCheckedVideos, syncAll, page } = this.state;

    const hasNoSelectedVideo = !Object.values(stateCheckedVideos).some((value: boolean) => value === true);

    return (
      <>
        <Container>
          <Cell width={12}>
            <Checkbox
              tag={`checkbox_${ovpType}_sync_check_all`}
              id="all"
              name="all"
              checked={syncAll}
              label="Check All"
              onChange={this.handleCheckAll}
            />
          </Cell>
        </Container>
        <Container columns={4} gap="1rem">
          {videos.map((item) => {
            const id = item.id || item.key;
            return (
              <Cell key={id}>
                <OvpVideoBox
                  tag={`checkbox_${ovpType}_sync_${id}`}
                  key={id}
                  onClick={this.toggleCheckbox(id)}
                  checked={stateCheckedVideos[id]}
                >
                  <Box>
                    <VideoCheckbox
                      id={id}
                      label=""
                      checked={!!stateCheckedVideos[id]}
                      onChange={this.toggleCheckbox(id)}
                    />
                    <VideoImage alt="preview_image" src={this.getImagePreview(item.images)} />
                    <Label title={item.name || item.title}>{truncate(item.name || item.title, { length: 15 })}</Label>
                  </Box>
                </OvpVideoBox>
              </Cell>
            );
          })}
        </Container>
        <StyledContainer margin="2rem 0 0 0">
          <Cell width={12}>
            <Pagination
              totalItems={total}
              numberOfPagesDisplayed={5}
              startPage={page}
              onPageChange={this.selectPage}
              itemsPerPage={8}
            />
          </Cell>
        </StyledContainer>
        <StyledContainer margin="2rem 0 0 0" columns={3} justifyContent="end">
          <Cell>
            <Button
              tag="button_back"
              fullWidth
              buttonModifiers={['buttonLink']}
              onClick={onBackButtonClick}
              type="button"
            >
              Back
            </Button>
          </Cell>
          <Cell left={3}>
            <Button
              tag="button_sync_videos"
              fullWidth
              buttonModifiers={['buttonPrimary']}
              disabled={hasNoSelectedVideo}
              onClick={onCompleteSelection(stateCheckedVideos, syncAll)}
              type="submit"
            >
              {isJwPlayerSync ? 'Next' : 'Sync Videos'}
            </Button>
          </Cell>
        </StyledContainer>
      </>
    );
  };

  render() {
    const {
      items: { isFetching },
    } = this.props;

    if (isFetching) {
      return <Loader />;
    }
    return this.renderContent();
  }
}

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  items: state.items,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  loadInitialExternalAssets: loadInitialExternalAssetsAction,
  loadBrightcoveExternalAssets: loadBrightcoveExternalAssetsAction,
  resetExternalAssets: resetExternalAssetsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(OvpOptionContent);
